import "@/App.css";
import "@/i18n";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/context/AuthContext";
import AccountSettings from "@/pages/AccountSettings";
import AdminAgencies from "@/pages/AdminAgencies";
import AdminApprovals from "@/pages/AdminApprovals";
import AdminAudit from "@/pages/AdminAudit";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminMissions from "@/pages/AdminMissions";
import AdminOrgs from "@/pages/AdminOrgs";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminTrials from "@/pages/AdminTrials";
import AdminUsers from "@/pages/AdminUsers";
import AgencyPublicProfile from "@/pages/AgencyPublicProfile";
import Cvtheque from "@/pages/Cvtheque";
import CvthequeDetail from "@/pages/CvthequeDetail";
import Dashboard from "@/pages/Dashboard";
import EmployerPublicProfile from "@/pages/EmployerPublicProfile";
import Home from "@/pages/Home";
import LegalCGV from "@/pages/LegalCGV";
import LegalTerms from "@/pages/LegalTerms";
import LegalPrivacy from "@/pages/LegalPrivacy";
import LegalCookies from "@/pages/LegalCookies";
import Login from "@/pages/Login";
import Marketplace from "@/pages/Marketplace";
import Messages from "@/pages/Messages";
import MissionComposer from "@/pages/MissionComposer";
import MissionDetail from "@/pages/MissionDetail";
import MyMissions from "@/pages/MyMissions";
import MySubmissions from "@/pages/MySubmissions";
import Notifications from "@/pages/Notifications";
import OrgSettings from "@/pages/OrgSettings";
import PasswordReset from "@/pages/PasswordReset";
import PasswordResetToken from "@/pages/PasswordResetToken";
import Roster from "@/pages/Roster";
import RosterForm from "@/pages/RosterForm";
import SignupAgency from "@/pages/SignupAgency";
import SignupAgencyInvite from "@/pages/SignupAgencyInvite";
import SignupEmployer from "@/pages/SignupEmployer";
import Subscription from "@/pages/Subscription";
import Team from "@/pages/Team";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/password-reset/token/:token" element={<PasswordResetToken />} />
          <Route path="/legal/cgv" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/legal/terms" element={<LegalTerms />} />
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          <Route path="/legal/cookies" element={<LegalCookies />} />
          <Route path="/signup/employer" element={<SignupEmployer />} />
          <Route path="/signup/agency" element={<SignupAgency />} />
          <Route path="/signup/agency/invite/:token" element={<SignupAgencyInvite />} />

          <Route
            path="/settings/account"
            element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/cvtheque"
            element={
              <ProtectedRoute roles={["employer", "agency", "admin"]}>
                <Cvtheque />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cvtheque/:id"
            element={
              <ProtectedRoute roles={["employer", "agency", "admin"]}>
                <CvthequeDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/roster"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <Roster />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roster/new"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <RosterForm mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roster/:id"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <RosterForm mode="edit" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/missions/mine"
            element={
              <ProtectedRoute roles={["employer", "agency"]} requireApprovedAgency>
                <MyMissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/missions/new"
            element={
              <ProtectedRoute roles={["employer", "agency"]} requireApprovedAgency>
                <MissionComposer mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/missions/:id/edit"
            element={
              <ProtectedRoute roles={["employer", "agency"]}>
                <MissionComposer mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/missions/:id"
            element={
              <ProtectedRoute>
                <MissionDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/marketplace"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <Marketplace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/marketplace/:id"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <MissionDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/organization"
            element={
              <ProtectedRoute roles={["employer", "agency"]}>
                <OrgSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/agencies/:id"
            element={
              <ProtectedRoute>
                <AgencyPublicProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employers/:id"
            element={
              <ProtectedRoute roles={["agency", "admin"]}>
                <EmployerPublicProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/agencies"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminAgencies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/approvals"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/missions"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminMissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orgs"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminOrgs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trials"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminTrials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <ProtectedRoute roles={["admin"]}>
                <AdminAudit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/subscription"
            element={
              <ProtectedRoute roles={["agency"]}>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/team"
            element={
              <ProtectedRoute roles={["agency"]}>
                <Team />
              </ProtectedRoute>
            }
          />
          <Route
            path="/submissions/mine"
            element={
              <ProtectedRoute roles={["agency"]} requireApprovedAgency>
                <MySubmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute roles={["employer", "agency"]}>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:threadId"
            element={
              <ProtectedRoute roles={["employer", "agency"]}>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
